// The Product Catalog: the Products app's first tab, the whole catalog as one
// tree for whoever looks after it. Family › product group › product, each in
// sort_order. Products drag into another group or place (POST
// /api/products/move); each one's prices, resourcing and billing open as hover
// cards, loaded when they open (GET /api/products/:id/facets). New Group opens
// the product group's New dialog here, so the group joins the tree in place.
import {
  Button,
  Card,
  CardBody,
  InlineMessage,
  Menu,
  MenuContent,
  MenuRadioGroup,
  MenuRadioItem,
  MenuTrigger,
  PageContent,
  PageHeader,
  PartIcon,
  RecordFormDialog,
  type RecordFormField,
  Spinner,
  Stack,
  SummaryTable,
  Text,
  TextLink,
} from '@olytic/foundation';
import { CatalogTree, type CatalogTreeFacet, type CatalogTreeFamily } from '@olytic/foundation/cpq';
import { ChevronDown, ChevronsDownUp, ChevronsUpDown, ListFilter, ListTree, Receipt, Rows3, Tag, Users } from 'lucide-react';
import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { authenticatedFetch } from '../auth/authClient';
import { objects } from '../config/objects';
import { useDocumentTitle } from '../documentTitle';
import { NEW_PRODUCT_ROUTE } from '../navigation';
import { humanize } from '../objects/format';
import { loadLookupOptions } from '../objects/lookupOptions';
import { objectMetaUrl, parseObjectMetaResponse } from '../objects/objectsApi';
import { recordPath } from '../objects/routes';
import { createBody, createRecord, formFields, type Writable } from '../objects/writes';
import {
  type CatalogFamily,
  type CatalogProduct,
  catalogAfterMove,
  type FacetGrid,
  type FacetPrice,
  type ProductFacets,
  type ProductStatus,
} from './productCatalog';
import { loadCatalog, loadFacets, moveProducts } from './productCatalogApi';

type Density = 'comfortable' | 'compact';

/** How far the tree is open: everything, families showing their groups, or families only. */
const OPENS = [
  { key: 'all', label: 'Expand All', icon: <ChevronsUpDown /> },
  { key: 'groups', label: 'Families and Groups', icon: <ListTree /> },
  { key: 'none', label: 'Collapse All', icon: <ChevronsDownUp /> },
] as const;
type Open = (typeof OPENS)[number]['key'];

/** Which product.status values the tree shows. */
const SHOWS: { key: string; label: string; statuses: ProductStatus[] }[] = [
  { key: 'all', label: 'Show All', statuses: ['draft', 'active', 'retired'] },
  { key: 'active', label: 'Active', statuses: ['active'] },
  { key: 'draft-active', label: 'Draft + Active', statuses: ['draft', 'active'] },
  { key: 'active-retired', label: 'Active + Retired', statuses: ['active', 'retired'] },
  { key: 'draft', label: 'Draft', statuses: ['draft'] },
  { key: 'retired', label: 'Retired', statuses: ['retired'] },
];

const SCHEME: Record<FacetPrice['scheme'], string> = { flat: 'Flat', per_hour: 'Per Hour', grid: 'Grid', included: 'Included' };
const PERIOD: Record<string, string> = { month: '/mo', year: '/yr' };
const money = (n: number) => n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
const yes = (b: boolean) => (b ? 'Yes' : 'No');

// One load per product, shared by its three cards.
const facetLoads = new Map<string, Promise<ProductFacets>>();
function facetsOf(id: string): Promise<ProductFacets> {
  if (!facetLoads.has(id)) {
    const load = loadFacets(id);
    load.catch(() => facetLoads.delete(id));
    facetLoads.set(id, load);
  }
  return facetLoads.get(id)!;
}

function useFacets(id: string) {
  const [state, setState] = useState<{ facets?: ProductFacets; error?: string }>({});
  useEffect(() => {
    let live = true;
    facetsOf(id).then(
      (facets) => { if (live) setState({ facets }); },
      (e: unknown) => { if (live) setState({ error: e instanceof Error ? e.message : 'Could not load.' }); },
    );
    return () => { live = false; };
  }, [id]);
  return state;
}

function amountOf(p: FacetPrice, grids: FacetGrid[]): string {
  if (p.scheme === 'grid') return grids.find((g) => g.id === p.grid_id)?.name ?? '—';
  if (p.scheme === 'included') return 'Included';
  if (p.scheme === 'per_hour') return `${money(p.amount ?? 0)}/h`;
  return `${money(p.amount ?? 0)}${p.period ? PERIOD[p.period] : ''}`;
}

function GridTable({ grid }: { grid: FacetGrid }) {
  const rows = grid.rows?.values ?? [];
  const cols = grid.cols?.values ?? [{ key: '', label: 'Amount' }];
  const cell = (row: string, col: string) => grid.cells.find((c) => c.key_1 === row && (grid.cols ? c.key_2 === col : true));
  return (
    <SummaryTable
      caption={grid.name}
      columns={[{ key: 'row', label: grid.rows?.label ?? '', numeric: false }, ...cols.map((c) => ({ key: `c:${c.key}`, label: c.label, numeric: true }))]}
      rows={rows.map((r) => ({
        key: r.key,
        cells: Object.fromEntries([['row', r.label], ...cols.map((c) => {
          const x = cell(r.key, c.key);
          return [`c:${c.key}`, x?.amount === null || x?.amount === undefined ? '—' : money(x.amount)];
        })]),
      }))}
    />
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Stack gap="xs">
      <Text tone="muted" size="sm">{title}</Text>
      {children}
    </Stack>
  );
}

/** What one hover card shows, read from the product's facets. */
function FacetContent({ id, facet, link }: { id: string; facet: 'prices' | 'resourcing' | 'billing'; link: (object: 'billing-policies', id: string, text: string) => ReactNode }) {
  const { facets, error } = useFacets(id);
  if (error) return <InlineMessage tone="error">{error}</InlineMessage>;
  if (!facets) return <Spinner label="Loading" />;

  if (facet === 'prices') {
    return (
      <Stack gap="md">
        <Section title="Prices">
          <SummaryTable
            caption="Prices"
            empty="No Prices"
            columns={[
              { key: 'label', label: 'Price', numeric: false, wrap: true },
              { key: 'model', label: 'Selling Model', numeric: false },
              { key: 'scheme', label: 'Scheme', numeric: false },
              { key: 'amount', label: 'Amount', numeric: true },
              { key: 'default', label: 'Default', numeric: true },
            ]}
            rows={facets.prices.map((p) => ({
              key: p.id,
              cells: { label: p.label ?? '—', model: p.selling_model, scheme: SCHEME[p.scheme] ?? humanize(p.scheme), amount: amountOf(p, facets.grids), default: yes(p.is_default) },
            }))}
          />
        </Section>
        {facets.grids.map((g) => <Section key={g.id} title={g.name}><GridTable grid={g} /></Section>)}
      </Stack>
    );
  }

  if (facet === 'resourcing') {
    const hours = facets.product.estimated_hours;
    return (
      <Stack gap="md">
        <SummaryTable caption="Estimated Hours" columns={[{ key: 'k', label: '', numeric: false }, { key: 'v', label: '', numeric: true }]} rows={[{ key: 'h', cells: { k: 'Estimated Hours', v: `${hours.toLocaleString('en-US')} h` } }]} />
        <Section title="Resource Roles">
          <SummaryTable
            caption="Resource Roles"
            empty="No Role Shares"
            columns={[
              { key: 'role', label: 'Role', numeric: false },
              { key: 'share', label: 'Share', numeric: true },
              { key: 'hours', label: 'Hours', numeric: true },
            ]}
            rows={facets.roleSplits.map((r) => ({
              key: r.id,
              cells: {
                role: r.name,
                share: r.share_pct === null ? '—' : `${Number(r.share_pct.toFixed(1))}%`,
                hours: r.share_pct === null ? '—' : `${Number(((hours * r.share_pct) / 100).toFixed(1))} h`,
              },
            }))}
          />
        </Section>
      </Stack>
    );
  }

  const { policy, treatments } = facets.billing;
  return (
    <Stack gap="md">
      <SummaryTable
        caption="Billing Policy"
        columns={[{ key: 'k', label: '', numeric: false }, { key: 'v', label: '', numeric: false }]}
        rows={[{ key: 'p', cells: { k: 'Billing Policy', v: policy ? link('billing-policies', policy.id, policy.name) : '—' } }]}
      />
      <Section title="Billing Treatments">
        <SummaryTable
          caption="Billing Treatments"
          empty="No Billing Treatments"
          columns={[
            { key: 'name', label: 'Treatment', numeric: false, wrap: true },
            { key: 'method', label: 'Method', numeric: false },
            { key: 'frequency', label: 'Frequency', numeric: false },
            { key: 'default', label: 'Default', numeric: true },
          ]}
          rows={treatments.map((t) => ({
            key: t.id,
            cells: {
              name: {
                text: t.name,
                note: t.items.length ? [...t.items].sort((a, b) => a.sequence - b.sequence).map((i) => `${Number((i.percent ?? 0).toFixed(2))}% ${i.label}`).join(' · ') : undefined,
              },
              method: humanize(t.method),
              frequency: humanize(t.billing_frequency),
              default: yes(t.is_default),
            },
          }))}
        />
      </Section>
    </Stack>
  );
}

const toTree = (catalog: CatalogFamily[], statuses: ProductStatus[]): CatalogTreeFamily<CatalogProduct & { isComponent: boolean }>[] =>
  catalog.map((f) => ({
    id: f.family,
    name: humanize(f.family),
    // Groups stay with none of their products showing: the taxonomy is the point.
    groups: f.groups.map((g) => ({
      id: g.id,
      code: g.code,
      name: g.name,
      products: g.products.filter((p) => statuses.includes(p.status)).map((p) => ({ ...p, isComponent: p.is_component })),
    })),
  }));

export function ProductCatalogPage(): JSX.Element {
  useDocumentTitle('Product Catalog');
  const navigate = useNavigate();
  const [catalog, setCatalog] = useState<CatalogFamily[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string> | null>(null);
  const [density, setDensity] = useState<Density>('compact');
  const [show, setShow] = useState('all');
  const shown = SHOWS.find((x) => x.key === show)!;

  const reload = () => loadCatalog().then(
    (c) => { setCatalog(c); setExpanded((e) => e ?? new Set(c.map((f) => f.family))); },
    (e: unknown) => setError(e instanceof Error ? e.message : 'Could not load the catalog.'),
  );
  useEffect(() => { void reload(); }, []);

  const tree = useMemo(() => (catalog ? toTree(catalog, shown.statuses) : []), [catalog, shown]);
  const families = tree.map((f) => f.id);
  const groups = tree.flatMap((f) => f.groups.map((g) => g.id));
  const keysFor = (o: Open): string[] => (o === 'all' ? [...families, ...groups] : o === 'groups' ? families : []);
  const sameAs = (keys: string[]) => expanded !== null && keys.length === expanded.size && keys.every((k) => expanded.has(k));
  const open = OPENS.find((o) => sameAs(keysFor(o.key)));

  const [newGroup, setNewGroup] = useState<{ fields: RecordFormField[]; writable: Writable } | null>(null);
  const startNewGroup = () => {
    setError(null);
    authenticatedFetch(objectMetaUrl('product-groups')).then(parseObjectMetaResponse).then(
      (meta) => {
        if (!meta.writable?.create) { setError('Product groups cannot be created.'); return; }
        setNewGroup({ fields: formFields(objects['product-groups'], meta.writable, loadLookupOptions), writable: meta.writable });
      },
      (e: unknown) => setError(e instanceof Error ? e.message : 'Could not open New Product Group.'),
    );
  };

  const link = (object: Parameters<typeof recordPath>[0], id: string, text: ReactNode) => {
    const href = recordPath(object, id);
    return (
      <TextLink href={href} onClick={(e) => { if (!e.metaKey && !e.ctrlKey && !e.shiftKey) { e.preventDefault(); navigate(href); } }}>
        {text}
      </TextLink>
    );
  };

  const move = (ids: string[], groupId: string, beforeId: string | null) => {
    setError(null);
    setCatalog((c) => (c ? catalogAfterMove(c, ids, groupId, beforeId) : c));
    moveProducts(ids, groupId, beforeId).then(
      () => { ids.forEach((id) => facetLoads.delete(id)); },
      (e: unknown) => { setError(e instanceof Error ? e.message : 'Could not move the products.'); void reload(); },
    );
  };

  const facets = (p: CatalogProduct): CatalogTreeFacet[] => [
    { key: 'prices', label: 'Prices', icon: <Tag />, wide: true, content: () => <FacetContent id={p.id} facet="prices" link={link} /> },
    { key: 'resourcing', label: 'Resourcing', icon: <Users />, content: () => <FacetContent id={p.id} facet="resourcing" link={link} /> },
    { key: 'billing', label: 'Billing', icon: <Receipt />, wide: true, content: () => <FacetContent id={p.id} facet="billing" link={link} /> },
  ];

  return (
    <PageContent as="section" width="fill" className="gap-4 px-6 pb-8 pt-5">
      <Card className="shrink-0 overflow-hidden">
        <PageHeader
          className="border-b-0"
          leading={<PartIcon className="size-[46px] text-[var(--brand-accent)]" />}
          title="Product Catalog"
          actions={(
            <>
              <Menu>
                <MenuTrigger asChild>
                  <Button variant="neutral" disabled={!catalog}>{open?.icon ?? <ChevronsUpDown />}{open?.label ?? 'Expand'}<ChevronDown /></Button>
                </MenuTrigger>
                <MenuContent align="end">
                  <MenuRadioGroup value={open?.key ?? ''} onValueChange={(v) => setExpanded(new Set(keysFor(v as Open)))}>
                    {OPENS.map((o) => <MenuRadioItem key={o.key} value={o.key}>{o.label}</MenuRadioItem>)}
                  </MenuRadioGroup>
                </MenuContent>
              </Menu>
              <Menu>
                <MenuTrigger asChild>
                  <Button variant="neutral"><ListFilter />{shown.label}<ChevronDown /></Button>
                </MenuTrigger>
                <MenuContent align="end">
                  <MenuRadioGroup value={show} onValueChange={setShow}>
                    {SHOWS.map((x) => <MenuRadioItem key={x.key} value={x.key}>{x.label}</MenuRadioItem>)}
                  </MenuRadioGroup>
                </MenuContent>
              </Menu>
              <Button variant="neutral" onClick={() => setDensity(density === 'compact' ? 'comfortable' : 'compact')}>
                <Rows3 />
                {density === 'compact' ? 'Compact' : 'Comfy'}
              </Button>
              <Button variant="neutral" disabled={!catalog} onClick={startNewGroup}>New Group</Button>
              <Button variant="brand" onClick={() => navigate(NEW_PRODUCT_ROUTE)}>New Product</Button>
            </>
          )}
        />
      </Card>
      {error ? <InlineMessage tone="error" role="alert">{error}</InlineMessage> : null}
      <Card>
        <CardBody>
          {!catalog || !expanded ? (
            error ? null : <div className="flex justify-center py-10"><Spinner label="Loading the catalog" /></div>
          ) : (
            <CatalogTree
              families={tree}
              rootLabel="Olytic Catalog"
              density={density}
              expanded={expanded}
              onExpandedChange={setExpanded}
              renderProduct={(p) => link('products', p.id, p.name)}
              renderGroup={(g) => link('product-groups', g.id, g.name)}
              productFacets={facets}
              onMove={move}
            />
          )}
        </CardBody>
      </Card>
      <RecordFormDialog
        open={newGroup !== null}
        onOpenChange={(o) => { if (!o) setNewGroup(null); }}
        title="New product group"
        fields={newGroup?.fields ?? []}
        submitLabel="Save"
        onSubmit={async (values) => {
          if (!newGroup) return;
          await createRecord(authenticatedFetch, 'product-groups', createBody(newGroup.writable, values));
          setNewGroup(null);
          // Open the new group's family so it shows where it landed.
          if (values.family) setExpanded((e) => new Set([...(e ?? []), values.family!]));
          await reload();
        }}
      />
    </PageContent>
  );
}
